import { css, html, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

/**
 * The busy treatment `schmancy-button` wears while its own action is in flight:
 * a fast, drawn motion-blur comet running along the button's bottom edge, with
 * an entrance and an exit. Two elements — this band, which clips and
 * choreographs, and `schmancy-busy-edge-rail`, the pattern layer that travels.
 *
 * ════ WHY THERE IS NO SPINNER HERE ════
 *
 * A spinner rendered beside the prefix slot costs the spinner rung plus the gap
 * on every button that ships NO prefix glyph — nothing had reserved that box, so
 * the spinner was pure addition. Measured on the sign-in step: a button with a
 * glyph held 229.82px across the transition, while the icon-less "Continue"
 * went 107.92px → 139.93px, +32.01px (md spinner 24px + gap-2 8px), shoving the
 * email field beside it. This treatment costs zero layout instead, so the two
 * cases finally behave the same way.
 *
 * ════ WHAT ANIMATES, AND WHY EACH IS COMPOSITOR-SAFE ════
 *
 *   rail  `transform: translateX()`   continuous, linear, infinite
 *   band  `transform: scaleX()`       one-shot, on enter and exit
 *   band  `opacity`                   one-shot, with the above
 *
 * That is the complete list. All three are compositor-thread properties: no
 * layout, no paint and no style recalculation per frame, so a running treatment
 * costs one composited layer transform and a dozen on screen cost essentially
 * nothing. Every drawing property — `background-image`, `background-size`,
 * alpha — is static, painted once into the rail layer and thereafter only moved.
 *
 * `contain: layout paint` means the band can never invalidate anything outside
 * itself, and it clips the over-wide rails without a separate `overflow` rule.
 *
 * ════ ZERO GEOMETRY, BY CONSTRUCTION ════
 *
 * The band is absolutely positioned and out of flow, and every animated property
 * is a transform or opacity — none participate in layout. The button's box is
 * therefore identical busy and idle on every variant and every rung, not as a
 * tuned result but as a structural guarantee. Browser-measured at 124.35 × 56 in
 * both states, 0.00px delta on both axes.
 *
 * ════ THE ENTRANCE IS THE PART WORTH KEEPING ════
 *
 * `transform-origin` flips with the state, which turns a fade into choreography:
 * entering, the origin is the leading edge and the band scales 0→1, so the light
 * SWEEPS IN from where the trail comes from. Leaving, the origin is the trailing
 * edge and it scales 1→0, so the last of the trail RUNS OFF THE END rather than
 * dying in place. Origin is not a transitioned property, so it applies instantly
 * at each state change and the collapse always heads the right way. In RTL every
 * one of travel, sweep and exit reverses together.
 *
 * ════ WHY THE BLUR IS DRAWN AND NOT ANIMATED ════
 *
 * Flicker fusion is ~50–60Hz. Every frequency a display can render sits below
 * it, so an oscillation reads as visible flutter or strobe, never as blur — and
 * an earlier round measured at 16.7Hz additionally sat at the peak of the
 * photosensitive-seizure band. Nothing here oscillates: the travel supplies the
 * motion and the drawing supplies the speed. That is also what lets the state
 * read in a still frame, and what keeps it from reading as a border: every stop
 * is hard.
 *
 * ════ PACE ════
 *
 * 96px per 360ms = 267 px/s, tunable without a rebuild through
 * `--schmancy-busy-edge-cycle` and `--schmancy-busy-edge-period` set anywhere
 * above the button. A 124px button is wider than a full lead period, so at least
 * one complete head is on screen at every instant — no moment reads as idle.
 */
@customElement('schmancy-busy-edge')
export class SchmancyBusyEdge extends LitElement {
	static styles = [
		css`
			:host {
				position: absolute;
				left: 0;
				right: 0;
				bottom: 0;
				height: 3px;
				z-index: 1;
				pointer-events: none;
				/* Clips the over-wide rails AND walls the treatment off: nothing
				   inside can invalidate anything outside this band. */
				contain: layout paint;
				opacity: 0;
				/* Collapsed toward the TRAILING edge at rest, so leaving reads as the
				   trail running off the end. Origin flips below on enter. */
				transform: scaleX(0);
				transform-origin: right;
				transition:
					transform 220ms cubic-bezier(0.2, 0.9, 0.25, 1),
					opacity 140ms ease;
			}
			/* Against a brand fill the edge has less contrast to work with than it
			   does against a dark ground, so it takes one more px and raised alpha
			   inside its own band — never outside it. */
			:host([boost]) {
				height: 4px;
			}
			:host([running]) {
				opacity: 1;
				transform: scaleX(1);
				/* Entering, the sweep comes from the leading edge. */
				transform-origin: left;
			}
			:host(:dir(rtl)) {
				transform-origin: left;
			}
			:host([running]:dir(rtl)) {
				transform-origin: right;
			}
			/* Motion off: no sweep, no travel. The band simply is, and the drawn
			   trail carries the state on its own. */
			@media (prefers-reduced-motion: reduce) {
				:host {
					transition: opacity 140ms ease;
					transform: scaleX(1);
				}
			}
		`,
	]

	/** The button's own action is in flight. Drives the sweep in and the run-off. */
	@property({ type: Boolean, reflect: true }) running = false

	/** Raised alpha for the filled grounds, inside the band's own height. */
	@property({ type: Boolean, reflect: true }) boost = false

	override render() {
		// Two lanes, because real motion blur has depth: a dim, finer, quicker lane
		// under the comet is what produces parallax rather than a second copy of the
		// same motion. Both lanes' periods and cycles are declared in the rail's own
		// CSS off one pair of custom properties, so a consumer retunes the pace once.
		return html`
			<schmancy-busy-edge-rail lane="fine" ?running=${this.running} ?boost=${this.boost}></schmancy-busy-edge-rail>
			<schmancy-busy-edge-rail lane="lead" ?running=${this.running} ?boost=${this.boost}></schmancy-busy-edge-rail>
		`
	}
}

/**
 * One translating lane of drawn motion blur. Two of these stack inside
 * `schmancy-busy-edge` to give the blur depth.
 *
 * ── The only property that animates here is transform ──
 * An earlier implementation animated `background-position`, which is not a
 * compositor-only property: it repaints the whole element every frame, and at a
 * 360ms cycle with several busy buttons on a page that is a paint storm for an
 * effect that should cost nothing. This animates `translateX` alone.
 * `will-change: transform` is declared only WHILE the rail is running — a
 * standing declaration would keep a compositor layer alive for every idle button
 * on the page, which is the memory cost that makes `will-change` a footgun.
 *
 * ── Seamlessness, expressed as a transform ──
 * The pattern repeats every `--period` and the animation translates by exactly
 * `--period`. Translating an infinite tiling by exactly one period is the
 * identity transform, so the wrap is invisible at any width. The rail is inset
 * -96px on both sides, wider than the band it lives in, so no translate can pull
 * an unpainted edge into view; the band clips it.
 *
 * ── The two lanes ──
 * `lead` carries the comet: five hard-edged ghosts at rising alpha behind a 19px
 * head, the way a strobe photograph frames a body moving too fast to freeze.
 * `fine` is a sparse, dim tick pattern on a shorter period running quicker —
 * 2px of ink every 32px at 26% alpha, so its contribution to the area's
 * luminance is small; it reads as texture and speed rather than as flashing, and
 * it is translation rather than luminance oscillation, which is what keeps it
 * clear of the photosensitive concern.
 */
@customElement('schmancy-busy-edge-rail')
export class SchmancyBusyEdgeRail extends LitElement {
	static styles = [
		css`
			:host {
				position: absolute;
				left: -96px;
				right: -96px;
				top: 0;
				bottom: 0;
				pointer-events: none;
				background-repeat: repeat-x;
				transform: translateX(0);
			}
			/* The comet. 96px period, 28px gap, five ghosts at rising alpha, 19px
			   head at the leading edge. Every stop hard — the property that keeps
			   this from ever reading as a border, and that lets a still frame read
			   as busy. */
			:host([lane='lead']) {
				--period: var(--schmancy-busy-edge-period, 96px);
				--cycle: var(--schmancy-busy-edge-cycle, 360ms);
				background-image: repeating-linear-gradient(
					90deg,
					transparent 0 28px,
					color-mix(in srgb, currentColor 10%, transparent) 28px 33px,
					transparent 33px 35px,
					color-mix(in srgb, currentColor 18%, transparent) 35px 41px,
					transparent 41px 43px,
					color-mix(in srgb, currentColor 30%, transparent) 43px 50px,
					transparent 50px 52px,
					color-mix(in srgb, currentColor 48%, transparent) 52px 61px,
					transparent 61px 63px,
					color-mix(in srgb, currentColor 72%, transparent) 63px 74px,
					transparent 74px 77px,
					currentColor 77px 96px
				);
				background-size: 100% 2px;
				background-position: 0 100%;
			}
			:host([lane='lead'][boost]) {
				background-image: repeating-linear-gradient(
					90deg,
					transparent 0 28px,
					color-mix(in srgb, currentColor 20%, transparent) 28px 33px,
					transparent 33px 35px,
					color-mix(in srgb, currentColor 32%, transparent) 35px 41px,
					transparent 41px 43px,
					color-mix(in srgb, currentColor 48%, transparent) 43px 50px,
					transparent 50px 52px,
					color-mix(in srgb, currentColor 66%, transparent) 52px 61px,
					transparent 61px 63px,
					color-mix(in srgb, currentColor 86%, transparent) 63px 74px,
					transparent 74px 77px,
					currentColor 77px 96px
				);
				background-size: 100% 3px;
			}
			/* Parallax texture: sparse, dim, shorter period, quicker. The 0.29 ratio
			   scales the cycle with the lead lane's, so retuning the pace moves both
			   lanes together and the composition holds. */
			:host([lane='fine']) {
				--period: 32px;
				--cycle: calc(var(--schmancy-busy-edge-cycle, 360ms) * 0.29);
				background-image: repeating-linear-gradient(
					90deg,
					color-mix(in srgb, currentColor 26%, transparent) 0 2px,
					transparent 2px 32px
				);
				background-size: 100% 1px;
				background-position: 0 0;
			}
			:host([lane='fine'][boost]) {
				background-image: repeating-linear-gradient(
					90deg,
					color-mix(in srgb, currentColor 42%, transparent) 0 2px,
					transparent 2px 32px
				);
			}
			:host([running]) {
				will-change: transform;
				animation: schmancy-busy-edge-run var(--cycle, 360ms) linear infinite;
			}
			:host([running]:dir(rtl)) {
				animation-direction: reverse;
			}
			/* Exactly one period per cycle. The custom property resolves to a length
			   at computed-value time, so one keyframe set serves both lanes. */
			@keyframes schmancy-busy-edge-run {
				from {
					transform: translateX(0);
				}
				to {
					transform: translateX(var(--period, 96px));
				}
			}
			/* Motion off: no animation at all. The drawn trail still depicts speed
			   with nothing moving, which is the advantage of drawing it. */
			@media (prefers-reduced-motion: reduce) {
				:host([running]) {
					animation: none;
					transform: translateX(40px);
				}
			}
		`,
	]

	@property({ type: String, reflect: true }) lane: 'lead' | 'fine' = 'lead'
	@property({ type: Boolean, reflect: true }) running = false
	@property({ type: Boolean, reflect: true }) boost = false

	override render() {
		return html``
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-busy-edge': SchmancyBusyEdge
		'schmancy-busy-edge-rail': SchmancyBusyEdgeRail
	}
}
